import React from 'react';
import { EnvelopeSimple } from '@phosphor-icons/react';
import UTILITIES from '../data/scientificUtilities';
import TI from './brand/TI';

/* ====================================================================
 * <ScientificUtilities /> — public scientific tools catalog on tagc.us.
 *
 * Data-driven from data/scientificUtilities.js. No layout/functionality
 * is hardcoded here beyond editorial presentation. Cards do NOT expose
 * calculator functionality on the public site — the catalog announces
 * capability and routes visitors either into ToxIntel Enterprise
 * (for "available" items) or into a consulting inquiry.
 * ==================================================================== */

const SLATE = '#0B1426';
const IVORY = '#F5F1E8';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const INK = '#1F2937';

const STATUS_LABELS = {
  available: 'Available',
  'expert-review': 'Expert review required',
  beta: 'Beta',
  planned: 'Planned',
};

function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || status;
  const isAvailable = status === 'available';
  const isPlanned = status === 'planned';
  const styles = isAvailable
    ? { borderColor: CORAL, color: CORAL, backgroundColor: 'rgba(255,78,106,0.06)' }
    : isPlanned
    ? { borderColor: '#D6CDB8', color: MUTED, backgroundColor: 'transparent' }
    : { borderColor: SLATE, color: SLATE, backgroundColor: 'rgba(11,20,38,0.05)' };
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[9.5px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border"
      style={styles}
      data-testid={`utility-status-${status}`}
    >
      <span className="w-1 h-1 rounded-full" style={{ backgroundColor: styles.color }} />
      {label}
    </span>
  );
}

function TrackLine({ track, status }) {
  let label;
  let toneToxintel = track === 'toxintel';
  if (track === 'toxintel') {
    if (status === 'available') label = <>Available in <TI /> Enterprise</>;
    else if (status === 'beta') label = <>In beta inside <TI /> Enterprise</>;
    else label = <>In development in <TI /> Enterprise</>;
  } else {
    label = <>Available through Scientific Consulting</>;
  }
  return (
    <p
      className="text-[10px] font-mono uppercase tracking-[0.22em] mt-3 pt-3 border-t"
      style={{
        color: toneToxintel && status === 'available' ? SLATE : MUTED,
        borderColor: '#E3D9C0',
      }}
      data-testid={`utility-track-${track}-${status}`}
    >
      {label}
    </p>
  );
}

function UtilityCard({ util }) {
  const contactHref = `mailto:regulatory@tagc.us?subject=${encodeURIComponent(
    `${util.name} — inquiry`
  )}&body=${encodeURIComponent(
    `Briefly: company, product category, jurisdictions, and the scientific question that prompted the call.\n\n(Utility: ${util.name})`
  )}`;
  const isEnterprise = util.track === 'toxintel' && util.status === 'available';
  return (
    <article
      className="border rounded-sm p-6 flex flex-col"
      style={{ borderColor: '#D6CDB8', backgroundColor: '#FBF7EE' }}
      data-testid={`utility-${util.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="font-serif text-[17px] leading-tight" style={{ color: SLATE }}>
          {util.name}
        </h4>
        <StatusBadge status={util.status} />
      </div>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: INK }}>
        {util.body}
      </p>

      <dl
        className="text-[11px] font-mono tracking-[0.05em] mb-3 space-y-1"
        style={{ color: MUTED }}
      >
        <div className="flex gap-2">
          <dt className="uppercase tracking-[0.2em] shrink-0 w-20">Service</dt>
          <dd className="normal-case" style={{ color: INK }}>
            {util.service}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="uppercase tracking-[0.2em] shrink-0 w-20">Industry</dt>
          <dd className="normal-case" style={{ color: INK }}>
            {util.industry}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="uppercase tracking-[0.2em] shrink-0 w-20">
            <TI /> module
          </dt>
          <dd className="normal-case" style={{ color: INK }}>
            {util.toxintelModule}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="uppercase tracking-[0.2em] shrink-0 w-20">Utility ID</dt>
          <dd className="normal-case" style={{ color: INK }}>
            {util.productManifest}
          </dd>
        </div>
      </dl>

      <TrackLine track={util.track} status={util.status} />

      <div className="mt-4 flex flex-wrap gap-2">
        {isEnterprise && (
          <a
            href="https://toxintel.tagc.us"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px"
            style={{ backgroundColor: SLATE, color: IVORY }}
            data-testid={`utility-enterprise-${util.productManifest.toLowerCase()}`}
          >
            Open in <TI />
          </a>
        )}
        <a
          href={contactHref}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider border transition-colors hover:bg-[#0B1426] hover:text-[#F5F1E8]"
          style={{ borderColor: SLATE, color: SLATE }}
          data-testid={`utility-inquire-${util.productManifest.toLowerCase()}`}
        >
          <EnvelopeSimple size={12} weight="bold" /> Inquire
        </a>
      </div>
    </article>
  );
}

export default function ScientificUtilities() {
  return (
    <section
      id="utilities"
      className="border-t"
      style={{ borderColor: '#E3D9C0' }}
      data-testid="tagc-scientific-utilities"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        {/* Section header */}
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
              data-testid="utilities-eyebrow"
            >
              04 &mdash; Scientific utilities
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]" style={{ color: SLATE }}>
              Practical scientific tools, mapped to consulting and platform capability.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-base lg:text-lg leading-relaxed mb-4" style={{ color: MUTED }}>
              A public catalog of the scientific utilities the practice engages, organised by
              scientific domain. Each utility declares its status, its ToxIntel Enterprise
              module (where available), and the consulting service that supports it.
            </p>
            <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>
              Items marked <em>Available in <TI /> Enterprise</em> operate as fully-supported
              workflows inside the platform. Items marked <em>Available through Scientific
              Consulting</em> are delivered as expert-led engagements. Nothing in this catalog
              is exposed as an automated public calculator on this website.
            </p>
          </div>
        </div>

        {/* Sections */}
        {UTILITIES.map((group) => (
          <div key={group.section} className="mb-14 last:mb-0">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.3em] mb-6 pb-3 border-b"
              style={{ color: SLATE, borderColor: '#D6CDB8' }}
              data-testid={`utilities-section-${group.section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {group.section}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.items.map((util) => (
                <UtilityCard key={util.name} util={util} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
